import { userErrorMessage } from '@wx-weiqi/contracts';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import type { EbookDocument } from '@wx-weiqi/contracts';
import { parseEbookPages } from '../services/ebook-pages';
import './ebook-page-picker.css';

const PAGE_GROUP_SIZE = 4;

export function EbookPagePicker({
  pages,
  page,
  onPage,
  onProcess,
  busy,
  budget,
  dirty,
}: {
  pages: EbookDocument['pages'];
  page: number;
  onPage: (index: number) => void;
  onProcess: (indexes: number[]) => void;
  busy: boolean;
  budget: number;
  dirty: boolean;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const [range, setRange] = useState('');
  const [error, setError] = useState('');
  const pageGroup = Math.floor(page / PAGE_GROUP_SIZE);
  const pageGroups = Math.max(1, Math.ceil(pages.length / PAGE_GROUP_SIZE));
  return (
    <>
      <label>
        PDF 页码
        <input
          value={range}
          placeholder="如 4-8,12,16"
          disabled={busy}
          onChange={(e) => setRange(e.target.value)}
        />
      </label>
      <div className="ebook-actions">
        <button
          disabled={busy}
          onClick={() => {
            try {
              setSelected(parseEbookPages(range, pages.length));
              setError('');
            } catch (e) {
              setError(userErrorMessage(e, '页码无效'));
            }
          }}
        >
          按页码勾选
        </button>
        <button
          disabled={busy || !selected.length}
          onClick={() => {
            setSelected([]);
            setError('');
          }}
        >
          清空勾选
        </button>
        <button
          className="primary"
          disabled={busy || !selected.length || selected.length > budget}
          onClick={() => onProcess(selected)}
        >
          {dirty ? '保存并识别所选页' : '识别所选页'}
        </button>
      </div>
      <div className="ebook-page-selection-summary">
        <p>
          已选 {selected.length} 页 · 每次最多 {budget} 页
        </p>
        <details className="ebook-page-rules">
          <summary>处理规则</summary>
          <p>只处理勾选页，其余等待中的任务停止；已完成页保留，不重复识别。</p>
        </details>
      </div>
      {(error || selected.length > budget) && (
        <p role="alert" className="ebook-error">
          {error || '所选页数超过本次预算，请分批勾选。'}
        </p>
      )}
      <div className="ebook-page-navigation" aria-label="PDF 页面列表">
        {pages.slice(pageGroup * PAGE_GROUP_SIZE, (pageGroup + 1) * PAGE_GROUP_SIZE).map((p) => (
          <div key={p.index} className="ebook-page-option">
            <input
              type="checkbox"
              aria-label={`处理第 ${p.index + 1} 页`}
              checked={selected.includes(p.index)}
              disabled={busy}
              onChange={(e) =>
                setSelected((old) =>
                  e.target.checked
                    ? [...old, p.index].sort((a, b) => a - b)
                    : old.filter((i) => i !== p.index),
                )
              }
            />
            <button
              aria-pressed={page === p.index}
              title={`第 ${p.index + 1} 页 · 已尝试 ${p.attempts} 次`}
              onClick={() => onPage(p.index)}
            >
              <span>第 {p.index + 1} 页</span>
              <small>
                {' '}
                ·{' '}
                {
                  {
                    QUEUED: '等待处理',
                    RUNNING: '正在识别',
                    FAILED: '识别失败',
                    SKIPPED: '尚未处理',
                    DONE: p.result?.candidates.length
                      ? `${p.result.candidates.length} 个题图`
                      : '未发现题图',
                  }[p.status]
                }
              </small>
            </button>
          </div>
        ))}
      </div>
      <nav className="ebook-page-list-pagination" aria-label="PDF 页码分页">
        <span>
          {pageGroup + 1} / {pageGroups} 组
        </span>
        <button
          aria-label="上一组PDF页面"
          disabled={busy || pageGroup === 0}
          onClick={() => onPage((pageGroup - 1) * PAGE_GROUP_SIZE)}
        >
          <ChevronLeftIcon />
        </button>
        <button
          aria-label="下一组PDF页面"
          disabled={busy || pageGroup + 1 >= pageGroups}
          onClick={() => onPage((pageGroup + 1) * PAGE_GROUP_SIZE)}
        >
          <ChevronRightIcon />
        </button>
      </nav>
    </>
  );
}
